<template>
    <div class="flex h-screen bg-gray-50">
      <SideBar class="flex-shrink-0" />
      <div class="container mx-auto px-4 lg:px-8">
        <div class="mb-8 mt-5 flex flex-wrap justify-between items-center gap-4">
          <div class="hidden md:block">
            <h1 class="text-3xl font-bold text-gray-900">Invoice Management</h1>
            <p class="text-gray-600 mt-2">Create new invoices and manage existing ones</p>
          </div>
          <div class="flex items-center gap-3 flex-wrap">
            <!-- User filter — only visible to admin users 1 & 14 -->
            <div v-if="canOverridePaid" class="flex items-center gap-2">
              <label class="text-sm font-medium text-gray-600 whitespace-nowrap">Filter by user:</label>
              <select
                v-model="filterUserId"
                @change="fetchWithFilter"
                class="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
              >
                <option :value="null">All users</option>
                <option v-for="user in allUsers" :key="user.id" :value="user.id">
                  {{ user.name || user.email || `User ${user.id}` }}
                </option>
              </select>
            </div>
            <button 
              @click="toggleCreateForm" 
              class="px-4 py-2 btn-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 flex items-center"
            >
              <PlusIcon v-if="!isFormVisible" class="w-5 h-5 mr-2" />
              <MinusIcon v-else class="w-5 h-5 mr-2" />
              <span>{{ isFormVisible ? 'Hide Form' : 'Create Invoice' }}</span>
            </button>
          </div>
        </div>
  
        <div v-show="isFormVisible" class="bg-white rounded-lg shadow-sm border mb-8">
          <div class="px-6 py-4 border-b border-gray-200">
            <h2 class="text-xl font-semibold text-gray-800">Create New Invoice</h2>
          </div>
          
          <form @submit.prevent="createInvoice" class="p-6 space-y-6">
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Name</label>
                <input 
                  v-model="form.storeName"
                  type="text" 
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" 
                  placeholder="Enter store name"
                >
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                <input 
                  v-model="form.email"
                  type="email" 
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" 
                  placeholder="customer@example.com"
                >
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                <input 
                  v-model="form.phone"
                  type="tel" 
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" 
                  placeholder="+1 (555) 123-4567"
                >
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Invoice Date</label>
                <input 
                  v-model="form.date"
                  type="date" 
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
              </div>
            </div>
  
            <!-- Billing Address (split inputs) -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Billing Address</label>
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <input v-model="form.addressLine1" type="text" placeholder="Address line 1"
                       class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                <input v-model="form.addressLine2" type="text" placeholder="Address line 2 (optional)"
                       class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                <input v-model="form.city" type="text" placeholder="City"
                       class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                <input v-model="form.state" type="text" placeholder="State/Province"
                       class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                <input v-model="form.zip" type="text" placeholder="ZIP/Postal Code"
                       class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                <input v-model="form.country" type="text" placeholder="Country" disabled="disabled"
                       class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" />
              </div>
            </div>
  
            <div>
              <div class="flex justify-between items-center mb-4">
                <h3 class="text-lg font-medium text-gray-800">Line Items</h3>
                <button 
                  type="button" 
                  @click="addLineItem" 
                  class="px-3 py-1.5 text-sm btn-primary rounded-md  focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                >
                  Add Item
                </button>
              </div>
              
              <div class="border border-gray-200 rounded-lg overflow-hidden">
                <!-- Header (Hidden on mobile) -->
                <div class="hidden md:grid bg-gray-50 px-4 py-3 grid-cols-12 gap-4 text-sm font-medium text-gray-700">
                  <template v-if="!isDirecttide">
                    <div class="col-span-2">Sku</div>
                    <div class="col-span-3">Description</div>
                    <div class="col-span-2">Quantity</div>
                    <div class="col-span-2">Unit Price</div>
                    <div class="col-span-2">Total</div>
                  </template>
                  <template v-else>
                    <div class="col-span-4">Product</div>
                    <div class="col-span-1">Quantity</div>
                    <div class="col-span-2">Unit Price</div>
                    <div class="col-span-3">Discount</div>
                    <div class="col-span-1">Total</div>
                  </template>
                  <div class="col-span-1">Action</div>
                </div>

                <!-- Items -->
                <div v-for="(item, index) in form.lineItems" :key="index" class="px-4 py-4 md:py-3 grid grid-cols-1 md:grid-cols-12 gap-4 border-b border-gray-100 last:border-0 relative">
                  
                  <template v-if="!isDirecttide">
                    <!-- SKU -->
                    <div class="col-span-1 md:col-span-2">
                      <label class="md:hidden block text-xs font-medium text-gray-500 mb-1">SKU</label>
                      <input 
                        v-model="item.sku"
                        @input="calculateTotal"
                        type="text" 
                        class="w-full px-2 py-1 border border-gray-300 rounded text-sm" 
                        placeholder="Sku"
                      >
                    </div>

                    <!-- Description -->
                    <div class="col-span-1 md:col-span-3">
                      <label class="md:hidden block text-xs font-medium text-gray-500 mb-1">Description</label>
                      <input 
                        v-model="item.description"
                        @input="calculateTotal"
                        type="text" 
                        class="w-full px-2 py-1 border border-gray-300 rounded text-sm" 
                        placeholder="Item description"
                      >
                    </div>

                    <div class="col-span-1 md:col-span-2">
                        <label class="md:hidden block text-xs font-medium text-gray-500 mb-1">Quantity</label>
                        <input 
                        v-model.number="item.quantity"
                        @input="calculateTotal"
                        type="number" 
                        class="w-full px-2 py-1 border border-gray-300 rounded text-sm text-center"
                        >
                    </div>
                    <div class="col-span-1 md:col-span-2">
                        <label class="md:hidden block text-xs font-medium text-gray-500 mb-1">Unit Price</label>
                        <input 
                        v-model.number="item.unitPrice"
                        step="0.01"
                        @input="calculateTotal"
                        type="number" 
                        class="w-full px-2 py-1 border border-gray-300 rounded text-sm text-right" 
                        placeholder="0.00"
                        >
                    </div>

                    <!-- Total -->
                    <div class="col-span-1 md:col-span-2 flex items-center justify-between md:block">
                      <label class="md:hidden text-xs font-medium text-gray-500">Total:</label>
                      <span class="text-sm font-bold md:font-medium">${{ getLineItemTotal(item).toFixed(2) }}</span>
                    </div>
                  </template>
                  
                  <template v-else>
                    <!-- Product Dropdown -->
                    <div class="col-span-1 md:col-span-4">
                      <label class="md:hidden block text-xs font-medium text-gray-500 mb-1">Product</label>
                      <select 
                        v-model="item.sku"
                        @change="onProductSelect(index, $event.target.value)"
                        class="w-full px-2 py-1 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
                      >
                        <option value="">Select a product</option>
                        <option v-for="prod in products" :key="prod.sku" :value="prod.sku">
                          {{ prod.name }} ({{ prod.sku }})
                        </option>
                      </select>
                    </div>

                    <div class="col-span-1 md:col-span-1">
                        <label class="md:hidden block text-xs font-medium text-gray-500 mb-1">Quantity</label>
                        <input 
                        v-model.number="item.quantity"
                        @input="calculateTotal"
                        type="number" 
                        class="w-full px-2 py-1 border border-gray-300 rounded text-sm text-center"
                        >
                    </div>

                    <div class="col-span-1 md:col-span-2">
                        <label class="md:hidden block text-xs font-medium text-gray-500 mb-1">Unit Price</label>
                        <input 
                        v-model.number="item.unitPrice"
                        step="0.01"
                        @input="calculateTotal"
                        type="number" 
                        class="w-full px-2 py-1 border border-gray-300 rounded text-sm text-right" 
                        placeholder="0.00"
                        >
                    </div>

                    <!-- Discount (Directtide only) -->
                    <div class="col-span-1 md:col-span-3">
                      <label class="md:hidden block text-xs font-medium text-gray-500 mb-1">Discount</label>
                      <div class="flex items-center space-x-1">
                        <select 
                          v-model="item.discountType"
                          @change="calculateTotal"
                          class="px-1 py-1 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-500 w-16"
                        >
                          <option value="flat">-$</option>
                          <option value="percent">-%</option>
                        </select>
                        <input 
                          v-model.number="item.discountValue"
                          @input="calculateTotal"
                          type="number" 
                          step="0.01" 
                          min="0"
                          class="w-full px-2 py-1 border border-gray-300 rounded text-sm text-right" 
                          placeholder="0.00"
                        >
                      </div>
                    </div>

                    <!-- Total -->
                    <div class="col-span-1 md:col-span-1 flex items-center justify-between md:block">
                      <label class="md:hidden text-xs font-medium text-gray-500">Total:</label>
                      <span class="text-sm font-bold md:font-medium">${{ getLineItemTotal(item).toFixed(2) }}</span>
                    </div>
                  </template>

                  <!-- Mobile Delete Button (Full width at bottom) -->
                  <div class="md:hidden col-span-1 pt-2 border-t border-gray-50 flex justify-end">
                     <button 
                      type="button" 
                      @click="removeLineItem(index)"
                      :disabled="form.lineItems.length <= 1"
                      :class="form.lineItems.length <= 1 ? 'text-gray-300 cursor-not-allowed hidden' : 'text-red-600 bg-red-50 hover:bg-red-100'"
                      class="flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors font-medium text-sm w-full justify-center"
                    >
                      <TrashIcon class="w-4 h-4" />
                      <span>Remove Item</span>
                    </button>
                  </div>

                  <!-- Desktop Delete Button -->
                  <div class="hidden md:block col-span-1">
                    <button 
                      type="button" 
                      @click="removeLineItem(index)" 
                      :disabled="form.lineItems.length <= 1"
                      :class="form.lineItems.length <= 1 ? 'text-gray-300 cursor-not-allowed' : 'text-red-600 hover:text-red-800'"
                      class="text-sm font-bold p-1"
                    >
                      <TrashIcon class="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
  
            <div>
              <div class="space-y-3">
                <div class="flex justify-between items-center">
                  <label class="text-sm font-medium text-gray-700">Subtotal:</label>
                  <span class="text-lg font-semibold">${{ subtotal.toFixed(2) }}</span>
                </div>
                
                <!-- Discount as percentage -->
                <div class="flex justify-between items-center" style="display: none;">
                  <label class="text-sm font-medium text-gray-700">Discount (%):</label>
                  <div class="flex items-center space-x-2">
                    <input 
                      v-model.number="form.discountPercent"
                      @input="calculateTotal"
                      type="number" 
                      step="1" 
                      min="0" 
                      class="w-24 px-2 py-1 border border-gray-300 rounded text-right" 
                      placeholder="0"
                    >
                    <span class="text-sm text-gray-600">= -${{ discountAmount.toFixed(2) }}</span>
                  </div>
                </div>
                
                <div class="flex justify-between items-center">
                  <label class="text-sm font-medium text-gray-700">Shipping:</label>
                  <div class="flex items-center space-x-2">
                    <span class="text-sm text-gray-600">$</span>
                    <input 
                      v-model.number="form.shipping"
                      @input="calculateTotal"
                      type="number" 
                      step="0.01" 
                      min="0" 
                      class="w-24 px-2 py-1 border border-gray-300 rounded text-right" 
                      placeholder="0.00"
                    >
                  </div>
                </div>

                <div v-if="isDirecttide" class="flex justify-between items-center">
                  <label class="text-sm font-medium text-gray-700">Discount:</label>
                  <div class="flex items-center space-x-2">
                    <select 
                      v-model="form.discountType"
                      @change="calculateTotal"
                      class="px-2 py-1 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <option value="flat">Flat ($)</option>
                      <option value="percent">Percentage (%)</option>
                    </select>
                    <span class="text-sm text-gray-600" v-if="form.discountType === 'flat'">$</span>
                    <input 
                      v-model.number="form.discountValue"
                      @input="calculateTotal"
                      type="number" 
                      step="0.01" 
                      min="0" 
                      class="w-24 px-2 py-1 border border-gray-300 rounded text-right" 
                      placeholder="0.00"
                    >
                    <span class="text-sm text-gray-600" v-if="form.discountType === 'percent'">%</span>
                    <span class="text-sm text-gray-500" v-if="form.discountType === 'percent'">(= -${{ directtideDiscountAmount.toFixed(2) }})</span>
                  </div>
                </div>
                
                <div class="border-t pt-3">
                  <div class="flex justify-between items-center">
                    <label class="text-lg font-bold text-gray-800">Total:</label>
                    <span class="text-xl font-bold text-green-600">${{ total.toFixed(2) }}</span>
                  </div>
                </div>
              </div>
            </div>
  
            <div class="flex justify-end">
              <button 
                type="button" 
                @click="clearForm" 
                class="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
              >
                Clear Form
              </button>
              <button 
                type="submit" 
                class="px-4 py-2 mx-2 text-sm btn-primary rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
              >
                Create Invoice
              </button>
            </div>
          </form>
        </div>
    
        <div class="bg-white rounded-lg shadow-sm border">
          <div class="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <div>
              <h2 class="text-xl font-semibold text-gray-800">Invoice History</h2>
              <p class="text-sm text-gray-600 mt-1">All previously created invoices</p>
            </div>
            <div class="text-sm text-gray-600">
                Total Invoices: <span class="font-semibold">{{ (invoices && invoices.length) || 0 }}</span>
            </div>
          </div>
          
          <!-- Mobile Card View -->
          <div class="md:hidden">
            <div v-if="invoices.length === 0" class="p-6 text-center text-gray-500 bg-gray-50 rounded-lg mx-4 mb-4">
              No invoices created yet. Click "Create Invoice" above to get started.
            </div>
            <div v-else class="space-y-4 px-4 pb-4">
              <div v-for="invoice in (invoices || [])" :key="invoice.id" class="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                <div class="flex justify-between items-start mb-3">
                  <div>
                    <span class="text-xs font-medium text-gray-500">#{{ invoice.id }}</span>
                    <h3 class="font-medium text-gray-900">{{ invoice.customer_first_name }} {{ invoice.customer_last_name }}</h3>
                    <p class="text-sm text-gray-500">{{ invoice.customer_email }}</p>
                  </div>
                  <div class="flex flex-col items-end gap-1">
                    <span
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold"
                      :class="{
                        'bg-green-100 text-green-800': isEffectivelyPaid(invoice),
                        'bg-gray-100 text-gray-800': !isEffectivelyPaid(invoice) && (invoice.status || '').toLowerCase() === 'attempted',
                        'bg-orange-100 text-orange-800': !isEffectivelyPaid(invoice) && (invoice.status || '').toLowerCase() === 'owed',
                        'bg-slate-100 text-slate-800': !isEffectivelyPaid(invoice) && !['paid','attempted','owed'].includes((invoice.status || '').toLowerCase())
                      }"
                    >
                      {{ isEffectivelyPaid(invoice) ? 'PAID' : (invoice.status || 'unknown').toUpperCase() }}
                    </span>
                    <span v-if="invoice.rocky_marked_paid" class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300">
                      ⚡ OVERRIDE
                    </span>
                  </div>
                </div>
                
                <div class="flex justify-between items-center py-2 border-t border-gray-100 mb-2">
                  <div class="text-sm text-gray-600">{{ invoice.invoice_date }}</div>
                  <div class="font-bold text-gray-900">${{ invoice.amount }}</div>
                </div>

                <div class="flex justify-end gap-2 flex-wrap">
                  <button
                    v-if="canOverridePaid"
                    @click="togglePaidMark(invoice)"
                    :disabled="loadingPaidIds.has(invoice.invoiceId)"
                    class="text-sm font-medium px-3 py-1.5 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    :class="invoice.rocky_marked_paid
                      ? 'text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-300'
                      : 'text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-300'"
                  >
                    {{ loadingPaidIds.has(invoice.invoiceId) ? '...' : (invoice.rocky_marked_paid ? 'Remove Paid Mark' : 'Mark as Paid') }}
                  </button>
                  <a
                    :href="`/public/invoice/${invoice.invoiceId || invoice.id}`"
                    target="_blank"
                    class="text-sm font-medium text-emerald-600 hover:text-emerald-900 bg-emerald-50 hover:bg-emerald-100 px-3 py-1.5 rounded transition-colors text-center flex items-center justify-center"
                  >
                    View Invoice
                  </a>
                  <button 
                     v-if="!String(invoice.invoiceId || invoice.id).startsWith('DT-')"
                     @click="copySharableLink(invoice.invoiceId || invoice.id)" 
                     class="text-sm font-medium text-blue-600 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded transition-colors"
                  >
                     {{ linkIsCopied == (invoice.invoiceId || invoice.id) ? 'Copied!' : 'Copy Sharable Link' }}
                  </button>
                  <button 
                     v-if="invoice.paymentLink"
                     @click="copyPaymentLink(invoice.paymentLink)" 
                     class="text-sm font-medium text-purple-600 hover:text-purple-900 bg-purple-50 hover:bg-purple-100 px-3 py-1.5 rounded transition-colors"
                  >
                     {{ linkIsCopied == invoice.paymentLink ? 'Copied!' : 'Payment Link' }}
                  </button>
                  <button
                    @click="confirmDeleteInvoice(invoice.invoiceId || invoice.id)"
                    class="text-sm font-medium text-red-600 hover:text-red-900 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded transition-colors flex items-center justify-center gap-1"
                  >
                    <TrashIcon class="w-4 h-4" />
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Desktop Table View -->
          <div class="hidden md:block overflow-x-auto">
            <table class="w-full">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice #</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-if="invoices.length === 0">
                  <td colspan="7" class="px-6 py-8 text-center text-gray-500">
                    No invoices created yet. Click "Create New Invoice" to get started.
                  </td>
                </tr>
                <tr v-for="invoice in (invoices || [])" :key="invoice.id" class="hover:bg-gray-50">
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">#{{ invoice.id }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ invoice.customer_first_name }} {{ invoice.customer_last_name }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ invoice.customer_email }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ invoice.invoice_date }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">${{ invoice.amount }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div class="flex flex-col gap-1">
                      <span
                          class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold"
                          :class="{
                          'bg-green-100 text-green-800': isEffectivelyPaid(invoice),
                          'bg-gray-100 text-gray-800': !isEffectivelyPaid(invoice) && (invoice.status || '').toLowerCase() === 'attempted',
                          'bg-orange-100 text-orange-800': !isEffectivelyPaid(invoice) && (invoice.status || '').toLowerCase() === 'owed',
                          'bg-slate-100 text-slate-800': !isEffectivelyPaid(invoice) && !['paid','attempted','owed'].includes((invoice.status || '').toLowerCase())
                          }"
                      >
                          {{ isEffectivelyPaid(invoice) ? 'PAID' : (invoice.status || 'unknown').toUpperCase() }}
                      </span>
                      <span v-if="invoice.rocky_marked_paid" class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300 w-fit">
                        ⚡ OVERRIDE
                      </span>
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div class="flex items-center gap-2 flex-wrap">
                        <button
                          v-if="canOverridePaid"
                          @click="togglePaidMark(invoice)"
                          :disabled="loadingPaidIds.has(invoice.invoiceId)"
                          class="text-xs font-medium px-2.5 py-1 rounded transition-colors border disabled:opacity-50 disabled:cursor-not-allowed"
                          :class="invoice.rocky_marked_paid
                            ? 'text-amber-700 bg-amber-50 hover:bg-amber-100 border-amber-300'
                            : 'text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border-emerald-300'"
                        >
                          {{ loadingPaidIds.has(invoice.invoiceId) ? '...' : (invoice.rocky_marked_paid ? 'Remove Paid Mark' : 'Mark as Paid') }}
                        </button>
                        <a 
                            :href="`/public/invoice/${invoice.invoiceId || invoice.id}`"
                            target="_blank"
                            class="text-emerald-600 hover:text-emerald-900"
                        >
                            View
                        </a>
                        <button 
                            v-if="!String(invoice.invoiceId || invoice.id).startsWith('DT-')"
                            @click="copySharableLink(invoice.invoiceId || invoice.id)" 
                            class="text-blue-600 hover:text-blue-900"
                        >
                            {{ linkIsCopied == (invoice.invoiceId || invoice.id) ? 'Copied!' : 'Copy Link' }}
                        </button>
                        <button 
                            v-if="invoice.paymentLink"
                            @click="copyPaymentLink(invoice.paymentLink)" 
                            class="text-purple-600 hover:text-purple-900"
                        >
                            {{ linkIsCopied == invoice.paymentLink ? 'Copied!' : 'Payment Link' }}
                        </button>
                        <button 
                            @click="confirmDeleteInvoice(invoice.invoiceId || invoice.id)" 
                            class="text-red-600 hover:text-red-900 flex items-center gap-0.5 ml-2"
                        >
                            <TrashIcon class="w-3.5 h-3.5" />
                            <span>Delete</span>
                        </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
  
      </div>
    </div>
  </template>
  
  <script setup>
  import { ref, computed, onMounted, watch } from 'vue'
  import { PlusIcon, MinusIcon, TrashIcon } from 'lucide-vue-next'
  import SideBar from '@/components/SideBar.vue'
  import Swal from 'sweetalert2'
  import { useStore } from 'vuex'
  import { fetchProducts } from '@/api/labels'
  const store = useStore()
  
  // Reactive data
  const invoices = computed(() => store.getters['invoices/allInvoices'] || [])
  const currentUser = computed(() => store.getters['auth/currentUser'])
  const canOverridePaid = computed(() => [1, 14].includes(currentUser.value?.id))
  const allUsers = computed(() => {
    const userOrg = String(currentUser.value?.organization ?? '').toLowerCase()
    return (store.getters['leads/allUsers'] || []).filter(u =>
      u.is_active == 1 && String(u.organization ?? '').toLowerCase() === userOrg
    )
  })
  const isFormVisible = ref(false)
  const linkIsCopied = ref(false);
  const loadingPaidIds = ref(new Set())
  const filterUserId = ref(null)

  const isDirecttide = computed(() => {
    return String(currentUser.value?.organization ?? '').toLowerCase() === 'directtide'
  })
  const products = ref([])

  watch(isDirecttide, async (val) => {
    if (val) {
      try {
        products.value = await fetchProducts()
      } catch (e) {
        console.error('Failed to fetch products for invoice creation:', e)
      }
    }
  }, { immediate: true })

  // Helper: treat invoice as paid if bankful says paid OR rocky_marked_paid is set
  const isEffectivelyPaid = (invoice) =>
    invoice.rocky_marked_paid != null || (invoice.status || '').toLowerCase() === 'paid'

  const fetchWithFilter = async () => {
    const params = filterUserId.value != null ? { filter_user_id: filterUserId.value } : {}
    try { await store.dispatch('invoices/fetchInvoices', params) } catch(e) { /* swallow */ }
  }

  const form = ref({
    storeName: '',
    email: '',
    phone: '',
    // split billing address
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zip: '',
    country: 'US',
    date: '',
    lineItems: [{
      description: '',
      quantity: 1,
      unitPrice: 0,
      sku: '',
      discountType: 'flat',
      discountValue: 0
    }],
    // percent discount
    discountPercent: 0,
    discountFee: 0,
    discountType: 'flat',
    discountValue: 0,
    shipping: 0
  })
  
  // Computed properties
  const getLineItemTotal = (item) => {
    const qty = Number(item.quantity || 0)
    const price = Number(item.unitPrice || 0)
    let base = qty * price
    const disc = Number(item.discountValue || 0)
    if (disc > 0) {
      if (item.discountType === 'percent') {
        base -= base * (disc / 100)
      } else {
        base -= disc
      }
    }
    return base
  }

  const subtotal = computed(() => {
    return form.value.lineItems.reduce((sum, item) => {
      return sum + getLineItemTotal(item)
    }, 0)
  })
  
  const discountAmount = computed(() =>
    subtotal.value * ((form.value.discountPercent || 0) / 100)
  )

  const directtideDiscountAmount = computed(() => {
    if (form.value.discountType === 'percent') {
      return subtotal.value * ((form.value.discountValue || 0) / 100)
    }
    return Number(form.value.discountValue || 0)
  })
  
  const total = computed(() => {
    if (isDirecttide.value) {
      return subtotal.value - directtideDiscountAmount.value + (form.value.shipping || 0)
    }
    return subtotal.value - discountAmount.value + (form.value.shipping || 0)
  })
  
  // Methods
  const toggleCreateForm = () => {
    isFormVisible.value = !isFormVisible.value
  }
  
  const addLineItem = () => {
    form.value.lineItems.push({
      description: '',
      quantity: 1,
      unitPrice: 0,
      sku: '',
      discountType: 'flat',
      discountValue: 0
    })
  }
  
  const removeLineItem = (index) => {
    if (form.value.lineItems.length <= 1) return
    form.value.lineItems.splice(index, 1)
    calculateTotal()
  }
  
  const calculateTotal = () => {
    // handled by computed; keep for consistency
  }

  const onProductSelect = (index, sku) => {
    const product = products.value.find(p => p.sku === sku)
    if (product) {
      form.value.lineItems[index].sku = product.sku
      form.value.lineItems[index].description = product.name
      form.value.lineItems[index].unitPrice = Number(product.price || 0)
    } else {
      form.value.lineItems[index].sku = ''
      form.value.lineItems[index].description = ''
      form.value.lineItems[index].unitPrice = 0
    }
    calculateTotal()
  }
  
  const clearForm = async () => {
    const res = await Swal.fire({
      title: 'Clear form?',
      text: 'This will remove all entered data.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#10b981',
      confirmButtonText: 'Yes, clear it'
    })
    if (!res.isConfirmed) return
  
    form.value = {
      storeName: '',
      email: '',
      phone: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zip: '',
      country: 'US',
      date: getTodayDate(),
      lineItems: [],
      discountPercent: 0,
      discountFee: 0,
      discountType: 'flat',
      discountValue: 0,
      shipping: 0
    }
    addLineItem() // Add one default line item
  }
  
    const createInvoice = async () => {
    if (!isDirecttide.value && form.value.discountPercent < 0) {
        await Swal.fire({ icon: 'error', title: 'Invalid Discount', text: 'Discount cannot be negative.' })
        return
    }
    if (isDirecttide.value && form.value.discountValue < 0) {
        await Swal.fire({ icon: 'error', title: 'Invalid Discount', text: 'Discount cannot be negative.' })
        return
    }

    if (!form.value.storeName || !form.value.email || !form.value.date) {
        await Swal.fire({ icon: 'error', title: 'Missing fields', text: 'Please fill in Store Name, Email, and Date.' })
        return
    }

    const validItems = form.value.lineItems.filter(i => i.description && i.quantity > 0)
    if (!validItems.length) {
        await Swal.fire({ icon: 'error', title: 'No items', text: 'Add at least one valid line item.' })
        return
    }

    const payload = {
        customer: {
        storeName: form.value.storeName,
        email: form.value.email,
        phone: form.value.phone
        },
        billingDetails: {
        first_name: form.value.storeName, // split later if you add name fields
        last_name: '',
        address_1: form.value.addressLine1,
        address_2: form.value.addressLine2,
        city: form.value.city,
        state: form.value.state,
        zip: form.value.zip,
        country: form.value.country,
        email: form.value.email,
        phone: form.value.phone
        },
        invoiceItems: validItems.map(i => ({
        item_name: i.description,
        item_description: i.description,
        sku: i.sku,
        quantity: Number(i.quantity),
        rate: Number(i.unitPrice),
        tax_percentage: 0,
        discountType: i.discountType || 'flat',
        discountValue: Number(i.discountValue || 0)
        })),
        invoice_date: form.value.date,
        reference_number: `INV-${Date.now()}`,
        request_currency: 'USD',
        discount: isDirecttide.value ? directtideDiscountAmount.value : form.value.discountPercent,
        discountType: isDirecttide.value ? form.value.discountType : 'percent',
        discountValue: isDirecttide.value ? form.value.discountValue : form.value.discountPercent,
        shipping_charge: form.value.shipping
    }

    try {
        const created = await store.dispatch('invoices/createInvoice', payload)
        // created should be what your BE returns (including Bankful IDs/links)
        const invId = created.invoiceId || created.id || created.bankful_invoice_id
        const publicUrl = `${window.location.origin}/public/invoice/${invId}`
        await Swal.fire({
        icon: 'success',
        title: `Invoice Created`,
        html: `
            <div style="text-align:left" class="space-y-2">
            <div><b>ID:</b> ${invId || '—'}</div>
            <div><b>Sharable Link:</b> <a href="${publicUrl}" target="_blank" class="text-blue-600 hover:underline">${publicUrl}</a></div>
            ${created.paymentLink ? `<div><b>Payment Link:</b> <a href="${created.paymentLink}" target="_blank" class="text-blue-600 hover:underline">${created.paymentLink}</a></div>` : ''}
            ${created.shortLink ? `<div><b>Short Link:</b> <a href="${created.shortLink}" target="_blank" class="text-blue-600 hover:underline">${created.shortLink}</a></div>` : ''}
            </div>
        `
        })
        await clearForm()
        toggleCreateForm()
        try { await store.dispatch('invoices/fetchInvoices') } catch(e) { /* optional toast */ }

    } catch (e) {
        await Swal.fire({ icon: 'error', title: 'Create failed', text: e?.message || 'Error creating invoice' })
    }
    }

  
/*   const viewInvoiceDetails = async (invoiceId) => {
    const invoice = invoices.value.find(inv => inv.id === invoiceId)
    if (invoice) {
      const itemsList = invoice.items.map(item => 
        `• ${item.description} - Qty: ${item.quantity} × $${item.unitPrice.toFixed(2)} = $${(item.quantity * item.unitPrice).toFixed(2)}`
      ).join('<br>')
  
      const invoiceSubtotal = invoice.total + invoice.discountAmount - invoice.shipping
      const addressBlock = (invoice.addressString || 'N/A').replace(/\n/g, '<br>')
  
      await Swal.fire({
        title: `Invoice #${invoice.id}`,
        html:
          `<div style="text-align:left;font-size:14px;line-height:1.4;">
            <strong>Store:</strong> ${invoice.storeName}<br>
            <strong>Email:</strong> ${invoice.email}<br>
            <strong>Phone:</strong> ${invoice.phone || 'N/A'}<br>
            <strong>Address:</strong><br>${addressBlock}<br>
            <strong>Date:</strong> ${invoice.date}<br><br>
            <strong>Items:</strong><br>${itemsList}<br><br>
            <strong>Subtotal:</strong> $${invoiceSubtotal.toFixed(2)}<br>
            <strong>Discount (${(invoice.discountPercent||0).toFixed(2)}%):</strong> -$${invoice.discountAmount.toFixed(2)}<br>
            <strong>Shipping:</strong> +$${invoice.shipping.toFixed(2)}<br>
            <strong>Total:</strong> $${invoice.total.toFixed(2)}
          </div>`,
        confirmButtonText: 'Close'
      })
    }
  } */
  
  const getTodayDate = () => {
    return new Date().toISOString().split('T')[0]
  }

  const copyPaymentLink = (link) => {
    if (!link) return;
    
    navigator.clipboard.writeText(link).then(() => {
      linkIsCopied.value = link;
      setTimeout(() => {
        linkIsCopied.value = false;
      }, 2000);
    });
  };

  const copySharableLink = (id) => {
    if (!id) return;
    const link = `${window.location.origin}/public/invoice/${id}`;
    navigator.clipboard.writeText(link).then(() => {
      linkIsCopied.value = id;
      setTimeout(() => {
        linkIsCopied.value = false;
      }, 2000);
    });
  };

  const togglePaidMark = async (invoice) => {
    const id = invoice.invoiceId ?? invoice.id
    if (loadingPaidIds.value.has(id)) return
    loadingPaidIds.value = new Set([...loadingPaidIds.value, id])
    try {
      if (invoice.rocky_marked_paid) {
        await store.dispatch('invoices/removeInvoicePaidMark', id)
      } else {
        await store.dispatch('invoices/markInvoicePaid', id)
      }
    } catch (e) {
      await Swal.fire({ icon: 'error', title: 'Error', text: e?.response?.data?.message || e?.message || 'Failed to update paid status' })
    } finally {
      const next = new Set(loadingPaidIds.value)
      next.delete(id)
      loadingPaidIds.value = next
    }
  }

  const confirmDeleteInvoice = async (id) => {
    const isDT = String(id).startsWith('DT-')
    const confirmText = isDT 
      ? 'Are you sure you want to delete this invoice? This will restore the items to your inventory ledger.'
      : 'Are you sure you want to delete this invoice?'

    const res = await Swal.fire({
      title: 'Delete Invoice?',
      text: confirmText,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    })

    if (!res.isConfirmed) return

    try {
      await store.dispatch('invoices/deleteInvoice', id)
      await Swal.fire({
        icon: 'success',
        title: 'Deleted!',
        text: 'The invoice has been soft-deleted and inventory calculations updated.',
        timer: 1500,
        showConfirmButton: false
      })
      await store.dispatch('invoices/fetchInvoices')
    } catch (e) {
      await Swal.fire({
        icon: 'error',
        title: 'Delete Failed',
        text: e?.response?.data?.error || e?.message || 'An error occurred while deleting the invoice.'
      })
    }
  }
  
  // Initialize component
  onMounted(async () => {
    form.value.date = getTodayDate()
    // Load user map for the filter dropdown (same pattern as GapsView)
    try { await store.dispatch('leads/fetchUserMap') } catch(e) { /* swallow */ }
    try { await store.dispatch('invoices/fetchInvoices') } catch(e) { /* optional toast */ }
  })
  </script>
  
  <style scoped>
  .btn-primary {
    background: #83f6b8;
    color: black;
    transition: all .2s ease;
  }
  </style>
  